import { useEffect, useRef, useState } from "react";
import { CreditCard } from "@phosphor-icons/react";
import { loadSquare } from "../lib/square";

// Renders Apple Pay / Google Pay wallet buttons (when supported) + the Square card form.
// All methods tokenize and call onToken(sourceId) — the parent charges via the backend.
export const SquarePayment = ({ amount, onToken, busy }) => {
  const cardRef = useRef(null);
  const cardInstance = useRef(null);
  const paymentsRef = useRef(null);
  const gpayRef = useRef(null);
  const applePayRef = useRef(null);
  const gpayElRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [gpayReady, setGpayReady] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  const [err, setErr] = useState("");

  const appId = process.env.REACT_APP_SQUARE_APP_ID;
  const locationId = process.env.REACT_APP_SQUARE_LOCATION_ID;

  // Load Square + card form once.
  useEffect(() => {
    let cancelled = false;
    loadSquare()
      .then(async (Square) => {
        if (cancelled || !cardRef.current) return;
        const payments = Square.payments(appId, locationId);
        paymentsRef.current = payments;
        const card = await payments.card();
        await card.attach(cardRef.current);
        cardInstance.current = card;
        if (!cancelled) setReady(true);
      })
      .catch(() => setErr("Card form couldn't load. Please choose Pay on pickup."));
    return () => {
      cancelled = true;
      try { cardInstance.current?.destroy(); } catch (e) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Re)initialise wallets whenever the charge amount (tip) changes.
  useEffect(() => {
    const payments = paymentsRef.current;
    if (!payments || !ready || !amount) return;
    let cancelled = false;
    (async () => {
      const req = payments.paymentRequest({
        countryCode: "CA",
        currencyCode: "CAD",
        total: { amount: Number(amount).toFixed(2), label: "DS CABS ride" },
      });
      // Google Pay
      try {
        const gp = await payments.googlePay(req);
        if (cancelled) return;
        if (gpayElRef.current) { gpayElRef.current.innerHTML = ""; await gp.attach(gpayElRef.current, { buttonColor: "black", buttonType: "long" }); }
        gpayRef.current = gp;
        setGpayReady(true);
      } catch (e) { setGpayReady(false); }
      // Apple Pay (renders our own button; requires Safari + registered domain)
      try {
        const ap = await payments.applePay(req);
        if (cancelled) return;
        applePayRef.current = ap;
        setAppleReady(true);
      } catch (e) { setAppleReady(false); }
    })();
    return () => { cancelled = true; try { gpayRef.current?.destroy?.(); } catch (e) { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, ready]);

  const walletPay = async (wallet) => {
    if (!wallet || busy) return;
    setErr("");
    try {
      const result = await wallet.tokenize();
      if (result.status === "OK") onToken(result.token);
      else setErr(result.errors?.[0]?.message || "Wallet payment couldn't complete.");
    } catch {
      setErr("Wallet payment couldn't complete. Please try your card.");
    }
  };

  const pay = async () => {
    if (!cardInstance.current) return;
    setErr("");
    try {
      const result = await cardInstance.current.tokenize();
      if (result.status !== "OK") {
        setErr(result.errors?.[0]?.message || "Please check your card details.");
        return;
      }
      onToken(result.token);
    } catch {
      setErr("Couldn't read the card. Please try again.");
    }
  };

  return (
    <div className="space-y-3">
      {/* Digital wallets */}
      {(gpayReady || appleReady) && (
        <div className="space-y-2" data-testid="wallet-buttons">
          {appleReady && (
            <button data-testid="apple-pay-btn" type="button" onClick={() => walletPay(applePayRef.current)} disabled={busy}
              className="w-full h-12 flex items-center justify-center gap-2 bg-ink text-white font-semibold border-2 border-ink hover:brightness-110 transition-all disabled:opacity-60">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16.365 1.43c0 1.14-.42 2.05-1.12 2.79-.84.9-1.86 1.42-2.96 1.33-.02-1.1.44-2.06 1.12-2.77.76-.79 2.02-1.35 2.96-1.35zM20.4 17.35c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.38 3.5-4.1 3.51-1.53.01-1.92-.99-4-.98-2.08.01-2.5.99-4.03.98-1.72-.01-3.04-1.76-4.03-3.32C-.2 16.5-.5 11.14 1.4 8.3c1.03-1.56 2.66-2.55 4.2-2.55 1.57 0 2.56 1 3.86 1 1.26 0 2.03-1 3.85-1 1.37 0 2.82.75 3.86 2.04-3.39 1.86-2.84 6.7.23 8.56z"/></svg>
              Pay with Apple Pay
            </button>
          )}
          <div ref={gpayElRef} data-testid="google-pay-btn" onClick={() => walletPay(gpayRef.current)}
            className={gpayReady ? "w-full min-h-[48px]" : "hidden"} />
          <div className="flex items-center gap-3 pt-1">
            <div className="h-px bg-ink/15 flex-1" /><span className="text-[10px] font-bold text-ink/40 uppercase tracking-wider">Or pay by card</span><div className="h-px bg-ink/15 flex-1" />
          </div>
        </div>
      )}

      <div ref={cardRef} data-testid="square-card" className="border-2 border-ink p-3 bg-white min-h-[56px]" />
      {err && <p data-testid="square-error" className="text-brandred text-xs font-semibold">{err}</p>}
      <button
        data-testid="square-pay-btn"
        type="button"
        onClick={pay}
        disabled={!ready || busy}
        className="w-full flex items-center justify-center gap-2 bg-brandblue text-white font-bold py-3.5 border-2 border-ink hover:brightness-110 transition-all disabled:opacity-60"
      >
        <CreditCard weight="fill" size={20} /> {busy ? "Processing…" : `Pay $${Number(amount).toFixed(2)} now`}
      </button>
    </div>
  );
};
